


















































import { Component, Prop, Vue } from 'vue-property-decorator';

// Importación de Componentes e Interfaces TypeScript
import PatientLoginView, { LoginPayload } from './GapPatientLogin.vue';
import DocumentList, { PatientInfo, DocumentItem } from './GapDocumentList.vue';

@Component({
  components: {
    PatientLoginView,
    DocumentList
  }
})
export default class PatientPortalView extends Vue {
  @Prop(String) readonly publicId!: string;

  // Claves para sessionStorage
  private readonly SESSION_KEY_PATIENT = 'gap_patient_session';
  private readonly SESSION_KEY_DOCS = 'gap_documents_session';

  // Estado de navegación: 'login' | 'documents'
  private currentStep: 'login' | 'documents' = 'login';
  
  // Flag interno de vigencia / existencia del enlace
  private isLinkExpired: boolean = false;
  private isLoadingOffice: boolean = false;

  // Datos de la oficina médica
  private officeName: string = 'Medical Specialty Center';
  private officeLogo: string = require('@/assets/wmpc_logo.svg');


  // Datos del paciente autenticado
  private activePatient: PatientInfo | null = null;

  // Lista de documentos recibidos
  private patientDocuments: DocumentItem[] = [];

  private loginErrorMessage: string = '';

  /**
   * Hook del ciclo de vida: Se ejecuta cuando se carga o refresca el componente
   */
  async created(): Promise<void> {
    await this.fetchOfficeData();
    this.restoreSession(); // Restaura la sesión activa si el usuario refrescó
  }

  /**
   * Restaura la sesión guardada desde sessionStorage si existe
   */
  private restoreSession(): void {
    try {
      const savedPatient = sessionStorage.getItem(this.SESSION_KEY_PATIENT);
      const savedDocs = sessionStorage.getItem(this.SESSION_KEY_DOCS);

      if (savedPatient && savedDocs) {
        this.activePatient = JSON.parse(savedPatient);
        this.patientDocuments = JSON.parse(savedDocs);
        this.currentStep = 'documents'; // Mantiene la pantalla de documentos activa
      }
    } catch (error) {
      console.error('Error restoring session:', error);
      this.clearSessionStorage();
    }
  }

  /**
   * Guarda los datos de autenticación en sessionStorage
   */
  private saveSession(patient: PatientInfo, docs: DocumentItem[]): void {
    try {
      sessionStorage.setItem(this.SESSION_KEY_PATIENT, JSON.stringify(patient));
      sessionStorage.setItem(this.SESSION_KEY_DOCS, JSON.stringify(docs));
    } catch (error) {
      console.error('Error saving session:', error);
    }
  }

  /**
   * Limpia los datos almacenados en sessionStorage
   */
  private clearSessionStorage(): void {
    sessionStorage.removeItem(this.SESSION_KEY_PATIENT);
    sessionStorage.removeItem(this.SESSION_KEY_DOCS);
  }

  /**
   * Carga los datos de la oficina médica usando el publicId
   */
  async fetchOfficeData(): Promise<void> {
    const id = this.publicId || (this.$route && (this.$route.query.publicId as string || this.$route.params.publicId as string));

    if (!id) {
      console.warn('No publicId provided in props or route.');
      this.isLinkExpired = true;
      return;
    }

    this.isLoadingOffice = true;
    this.isLinkExpired = false;

    try {
      const response = await this.$store.dispatch('gaps/getOfficeInfo', { publicId: id });

      if (response) {
        if (response.name) this.officeName = response.name;
        if (response.logoId) this.officeLogo = this.getLogoURL(response.logoId);
      }
    } catch (error) {
      console.error('Error fetching office data:', error);

      if ((error as any)?.response?.status === 404 || (error as any)?.status === 404) {
        this.isLinkExpired = true;
      } else {
        this.isLinkExpired = true;
      }
    } finally {
      this.isLoadingOffice = false;
    }
  }

  /**
   * Reintenta la carga en caso de fallo manual
   */
  async retryFetchOffice(): Promise<void> {
    await this.fetchOfficeData();
  }

  /**
   * Helper para formatear el tamaño en bytes a KB o MB
   */
  private formatFileSize(bytes: number): string {
    if (!bytes || bytes === 0) return '0 Bytes';
    const k = 1024;
    const sizes = ['Bytes', 'KB', 'MB', 'GB'];
    const i = Math.floor(Math.log(bytes) / Math.log(k));
    return parseFloat((bytes / Math.pow(k, i)).toFixed(1)) + ' ' + sizes[i];
  }

  /**
   * Helper para determinar el 'fileType' ('pdf' | 'image' | 'doc' | 'default')
   */
  private mapFileType(contentType: string, fileName: string): 'pdf' | 'image' | 'doc' | 'default' {
    if (contentType?.includes('pdf') || fileName?.endsWith('.pdf')) {
      return 'pdf';
    }
    if (contentType?.includes('image') || /\.(png|jpg|jpeg|gif|webp)$/i.test(fileName)) {
      return 'image';
    }
    if (
      contentType?.includes('word') || 
      contentType?.includes('officedocument') || 
      /\.(doc|docx)$/i.test(fileName)
    ) {
      return 'doc';
    }
    return 'default';
  }

  /**
   * Helper para formatear la fecha ISO a un formato legible (ej. "Aug 04, 2026")
   */
  private formatDate(dateString: string): string {
    if (!dateString) return 'N/A';
    const date = new Date(dateString);
    return date.toLocaleDateString('en-US', {
      month: 'short',
      day: '2-digit',
      year: 'numeric'
    });
  }

  /**
   * Maneja el inicio de sesión enviando la petición al backend real
   */
  async handlePatientLogin(payload: LoginPayload): Promise<void> {
    try {
      this.loginErrorMessage = '';
      payload.publicId = this.publicId;
      //console.log('Sending authentication to GAP Backend...', payload);

      const response = await this.$store.dispatch('gaps/getPatientGapFiles', payload);

      if (response && response.success) {
        
        // Mapear datos del paciente
        this.activePatient = {
          fullName: `${response.patient.firstName} ${response.patient.lastName}`.trim(),
          phone: payload.phone,
          dob: this.formatDate(payload.dob),
          recordId: `WKL-${response.patient.gapId}`
        };

        // Mapear lista de archivos recibida
        this.patientDocuments = (response.files || []).map((file: any) => ({
          id: file.id.toString(),
          name: file.name,
          size: this.formatFileSize(file.size),
          uploadDate: this.formatDate(file.created),
          fileType: this.mapFileType(file.contentType, file.name),
          downloadUrl: `/api/files/download/${file.storageId}`,
          storageId: file.storageId
        }));

        // Guardar sesión para persistencia al refrescar (F5)
        this.saveSession(this.activePatient, this.patientDocuments);

        // Registrar log de autenticación
        this.handleLogEvent({
          action: 'PATIENT_AUTHENTICATED',
          patientId: this.activePatient.recordId,
          gapId: response.patient.gapId,
          timestamp: new Date().toISOString()
        });

        // Cambiar a la vista de documentos
        this.currentStep = 'documents';

      } else {

        // Manejo específico si el mensaje es PATIENT_NOT_FOUND
      if (response && (response.message === 'PATIENT_NOT_FOUND' || response.success === false)) {
        this.loginErrorMessage = 'The entered Date of Birth or Phone Number is invalid. Please verify and try again.';
      } else {
        this.loginErrorMessage = 'Unable to verify your information. Please check your entries.';
      }

        console.warn('Authentication failed:', response);
        this.$emit('login-error', response?.message || 'PATIENT_NOT_FOUND');
      }

    } catch (error) {
      // Hacemos el casting a 'any' en una constante o directamente al acceder
      const err = error as any;

      // 1. Verificar si la respuesta vino del servidor (Hubo una respuesta HTTP con status 4xx/5xx)
      if (err.response) {
        const statusCode = err.response.status; // Código HTTP (ej: 404, 500, 400)
        const serverData = err.response.data;   // Contenido enviado por el servidor

        console.log('Código HTTP Status:', statusCode);
        console.log('Contenido de la respuesta:', serverData);

        // 2. Condición específica para Error 404
        if (statusCode === 404) {
          const customMessage = serverData?.message || serverData;

          if (customMessage === 'PATIENT_NOT_FOUND') {
            this.loginErrorMessage = 'The entered Date of Birth or Phone Number is invalid. Please verify and try again.';
          } else {
            this.loginErrorMessage = 'Patient record not found. Please check your credentials.';
          }
        } else {
          this.loginErrorMessage = 'An error occurred while connecting to the server. Please try again.';
        }

      } else if (err.request) {
        // La petición se realizó pero no se recibió respuesta (Error de red)
        console.error('No response received from server:', err.request);
        this.loginErrorMessage = 'Network error. Please check your connection.';
      } else {
        // Error en la configuración de la petición
        console.error('Error config:', err.message);
        this.loginErrorMessage = 'An error occurred while preparing the request.';
      }

      this.$emit('login-error', this.loginErrorMessage);
    }
  }

  /**
   * Maneja la salida o cierre de sesión y borra el almacenamiento local
   */
  handleLogout(): void {
    this.clearSessionStorage();
    this.activePatient = null;
    this.patientDocuments = [];
    this.currentStep = 'login';
  }

  /**
   * Captura y procesa todos los eventos de log
   */
  handleLogEvent(logData: Record<string, unknown>): void {
    console.log('[GAP LOG SERVICE]:', logData);
  }

  getLogoURL(id: string | number): string {
    return process.env.VUE_APP_ROOT_API + "/schedule/wmpc/content/file/" + id;
  }
}
